import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccessPage() {
  const [status, setStatus] = useState({ state: "checking" });
  const nav = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", amount: data.amount });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus({ state: "failed" });
          return;
        }
        if (attempts > 15) { setStatus({ state: "timeout" }); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts > 15) { setStatus({ state: "failed" }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-8">
      <Card className="max-w-md w-full p-8 text-center">
        {status.state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-zinc-400" />
            <div className="mt-4 font-medium">Confirming payment…</div>
            <div className="text-sm text-muted-foreground mt-1">This usually takes a few seconds.</div>
          </>
        )}
        {status.state === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
            <div className="mt-4 text-2xl font-semibold font-display">Payment received</div>
            <div className="text-sm text-muted-foreground mt-1">
              {status.amount ? `$${Number(status.amount).toFixed(2)} posted to your account.` : "Your ledger has been updated."}
            </div>
            <Button data-testid="pay-back-dashboard" onClick={() => nav("/resident/dashboard")} className="mt-6 rounded-full">Back to dashboard</Button>
          </>
        )}
        {(status.state === "failed" || status.state === "timeout") && (
          <>
            <div className="text-lg font-medium">We couldn&apos;t confirm your payment.</div>
            <div className="text-sm text-muted-foreground mt-1">Please check your ledger or try again.</div>
            <Button onClick={() => nav("/resident/pay")} className="mt-6 rounded-full">Try again</Button>
          </>
        )}
      </Card>
    </div>
  );
}
